import React, { useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Fade,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { format, isToday, isYesterday } from "date-fns";

import { i18n } from "../../translate/i18n";
import AudioMessageComponent from "../AudioMessageComponent";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  messagesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2)
  },
  dateHeader: {
    textAlign: "center",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(2, 4),
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  transcriptionPreview: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    alignSelf: "flex-end",
    maxWidth: 400,
    minWidth: 280,
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      right: -8,
      width: 0,
      height: 0,
      borderLeft: `8px solid ${theme.palette.primary.main}`,
      borderTop: "8px solid transparent"
    }
  },
  transcriptionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main
  },
  transcriptionText: {
    fontStyle: "italic",
    color: theme.palette.text.secondary,
    minHeight: 20
  },
  processingIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.light + "20",
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1, 0),
    color: theme.palette.warning.dark
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(4)
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    opacity: 0.5
  }
}));

const VoiceMessageHistory = ({
  messages = [],
  currentTranscription = "",
  isProcessing = false,
  onMessageAction
}) => {
  const classes = useStyles();
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);

  // Group messages by date
  const groupedMessages = useCallback(() => {
    const groups = {};
    
    messages.forEach(message => {
      const date = new Date(message.timestamp);
      const dateKey = format(date, "yyyy-MM-dd");
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          date,
          messages: []
        };
      }
      
      groups[dateKey].messages.push(message);
    });
    
    return Object.values(groups).sort((a, b) => a.date - b.date);
  }, [messages]);

  // Format date header
  const formatDateHeader = useCallback((date) => {
    if (isToday(date)) {
      return "Hoje";
    } else if (isYesterday(date)) {
      return "Ontem";
    } else {
      return format(date, "dd/MM/yyyy");
    }
  }, []);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "end"
      });
    }
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, currentTranscription, scrollToBottom]);

  // Handle message actions
  const handleMessageAction = useCallback((action, message) => {
    if (onMessageAction) {
      onMessageAction(action, message);
    }
  }, [onMessageAction]);

  const messageGroups = groupedMessages();

  return (
    <Box className={classes.root} ref={containerRef}>
      {messages.length === 0 && !currentTranscription && !isProcessing ? (
        // Empty state
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIcon}>🎙️</Box>
          <Typography variant="h6" gutterBottom>
            Nenhuma conversa ainda
          </Typography>
          <Typography variant="body2">
            Inicie uma conversa por voz clicando no botão de gravação
          </Typography>
        </Box>
      ) : (
        // Messages
        <Box className={classes.messagesContainer}>
          {messageGroups.map((group, groupIndex) => (
            <Box key={group.date.toISOString()}>
              {/* Date Header */}
              <Typography className={classes.dateHeader}>
                {formatDateHeader(group.date)}
              </Typography>
              
              {/* Messages for this date */}
              {group.messages.map((message) => (
                <AudioMessageComponent
                  key={message.id}
                  message={message}
                  isUser={message.type === "user"}
                  onShare={(msg) => handleMessageAction("share", msg)}
                  onDownload={(msg) => handleMessageAction("download", msg)}
                />
              ))}
            </Box>
          ))}

          {/* Current transcription preview */}
          {currentTranscription && (
            <Fade in={true}>
              <Paper className={classes.transcriptionPreview} elevation={2}>
                <Box className={classes.transcriptionHeader}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">
                    {i18n.t("voiceAssistant.voiceInterface.transcription")}...
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  className={classes.transcriptionText}
                >
                  {currentTranscription || "Transcrevendo..."}
                </Typography>
              </Paper>
            </Fade>
          )}

          {/* Processing indicator */}
          {isProcessing && (
            <Fade in={true}>
              <Box className={classes.processingIndicator}>
                <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                <Typography variant="body2">
                  {i18n.t("voiceAssistant.streaming.processing")}
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </Box>
      )}
    </Box>
  );
};

export default VoiceMessageHistory;